/**
 * Simulation Service — Annual Simulation API Client
 *
 * Calls the project-aware annual simulation endpoints on Flux EMS API.
 * Base path: /api/flux_ems/projects/{projectId}/annual-simulation
 */

import { API_BASE_URL } from './api';

function basePath(projectId) {
    return `${API_BASE_URL}/api/flux_ems/projects/${projectId}/annual-simulation`;
}

async function getToken(currentUser) {
    if (!currentUser) {
        throw new Error('Authentication required. Please log in.');
    }
    return currentUser.getIdToken();
}

function headers(token) {
    return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
    };
}

async function handleResponse(response) {
    let data;
    try {
        data = await response.json();
    } catch (e) {
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        throw new Error('Invalid JSON response from server');
    }
    if (!response.ok) {
        const msg = data.error || data.message || data.detail || `HTTP ${response.status}: ${response.statusText}`;
        const err = new Error(msg);
        err.status = response.status;
        err.data = data;
        throw err;
    }
    return data;
}

/**
 * Start an annual simulation for a project.
 * The backend loads facility config from the unified project automatically.
 *
 * @param {string} projectId - Unified project ID
 * @param {Object} [overrides] - Optional overrides
 * @param {number} [overrides.site_capacity_kw] - Site transformer capacity
 * @param {number} [overrides.demand_charge_usd_kw] - Demand charge rate
 * @param {number} [overrides.forecast_mae] - Forecast MAE (deg F)
 * @param {number|null} [overrides.forecast_seed] - Random seed
 * @param {string} [overrides.name] - Run name
 * @param {Object} currentUser - Firebase user
 * @returns {Promise<Object>} {run_id, status, agents}
 */
export async function startAnnualSimulation(projectId, overrides, currentUser) {
    const token = await getToken(currentUser);
    const response = await fetch(basePath(projectId), {
        method: 'POST',
        headers: headers(token),
        body: JSON.stringify(overrides || {}),
    });
    return handleResponse(response);
}

/**
 * Get status/results of an annual simulation run.
 *
 * @param {string} projectId - Unified project ID
 * @param {string} runId - Simulation run ID
 * @param {Object} currentUser - Firebase user
 * @returns {Promise<Object>} Full simulation run object
 */
export async function getAnnualSimulation(projectId, runId, currentUser) {
    const token = await getToken(currentUser);
    const response = await fetch(`${basePath(projectId)}/${runId}`, {
        method: 'GET',
        headers: headers(token),
    });
    return handleResponse(response);
}

/**
 * List all annual simulation runs for a project.
 *
 * @param {string} projectId - Unified project ID
 * @param {Object} currentUser - Firebase user
 * @returns {Promise<Object>} {runs: [...]}
 */
export async function listAnnualSimulations(projectId, currentUser) {
    const token = await getToken(currentUser);
    const response = await fetch(basePath(projectId), {
        method: 'GET',
        headers: headers(token),
    });
    return handleResponse(response);
}

/**
 * Delete (archive) an annual simulation run.
 *
 * @param {string} projectId - Unified project ID
 * @param {string} runId - Simulation run ID
 * @param {Object} currentUser - Firebase user
 * @returns {Promise<Object>}
 */
export async function deleteAnnualSimulation(projectId, runId, currentUser) {
    const token = await getToken(currentUser);
    const response = await fetch(`${basePath(projectId)}/${runId}`, {
        method: 'DELETE',
        headers: headers(token),
    });
    return handleResponse(response);
}

/**
 * Cancel a running annual simulation.
 *
 * @param {string} projectId - Unified project ID
 * @param {string} runId - Simulation run ID
 * @param {Object} currentUser - Firebase user
 * @returns {Promise<Object>}
 */
export async function cancelAnnualSimulation(projectId, runId, currentUser) {
    const token = await getToken(currentUser);
    const response = await fetch(`${basePath(projectId)}/${runId}/cancel`, {
        method: 'POST',
        headers: headers(token),
    });
    return handleResponse(response);
}

/**
 * Poll an annual simulation until it reaches a terminal state.
 *
 * @param {string} projectId - Unified project ID
 * @param {string} runId - Simulation run ID
 * @param {Object} currentUser - Firebase user
 * @param {Function} onProgress - Called with run state on each poll
 * @param {number} [interval=3000] - Poll interval in ms
 * @returns {Promise<Object>} Final simulation state
 */
export function pollAnnualSimulation(projectId, runId, currentUser, onProgress, interval = 3000) {
    let cancelled = false;
    let lastRun = null;
    let resolveRef = null;

    const promise = new Promise((resolve, reject) => {
        resolveRef = resolve;
        const poll = async () => {
            if (cancelled) {
                resolve(lastRun || { id: runId, status: 'cancelled' });
                return;
            }
            try {
                const run = await getAnnualSimulation(projectId, runId, currentUser);
                lastRun = run;

                if (onProgress) {
                    onProgress(run);
                }

                const terminal = ['completed', 'failed', 'cancelled'];
                if (terminal.includes(run.status)) {
                    resolve(run);
                    return;
                }

                setTimeout(poll, interval);
            } catch (err) {
                if (cancelled) {
                    resolve(lastRun || { id: runId, status: 'cancelled' });
                } else {
                    reject(err);
                }
            }
        };

        poll();
    });

    promise.cancel = () => {
        cancelled = true;
        // Resolve immediately so awaiting code unblocks
        if (resolveRef) {
            resolveRef({ ...(lastRun || { id: runId }), status: 'cancelled' });
        }
    };
    return promise;
}

/**
 * Get available scenarios for each app (Site DNA, Flux EV).
 *
 * @param {string} projectId - Unified project ID
 * @param {Object} currentUser - Firebase user
 * @returns {Promise<Object>} {site_dna: [...], ev: [...]}
 */
export async function getSimulationScenarios(projectId, currentUser) {
    const token = await getToken(currentUser);
    const response = await fetch(
        `${API_BASE_URL}/api/flux_ems/projects/${projectId}/simulation-scenarios`,
        {
            method: 'GET',
            headers: headers(token),
        },
    );
    return handleResponse(response);
}

/**
 * Get facility summary for a project (which agents are configured).
 *
 * @param {string} projectId - Unified project ID
 * @param {Object} currentUser - Firebase user
 * @returns {Promise<Object>} {project_id, project_name, site_capacity_kw, location, agents, has_agents}
 */
export async function getFacilitySummary(projectId, currentUser) {
    const token = await getToken(currentUser);
    const response = await fetch(
        `${API_BASE_URL}/api/flux_ems/projects/${projectId}/facility-summary`,
        {
            method: 'GET',
            headers: headers(token),
        },
    );
    return handleResponse(response);
}
